import { useMutation } from '@apollo/client';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useToast } from 'hephaestus/web';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { getBulkTradeRunDetailPath } from '../paths';
import { GENERATE_BULK_TRADES } from '../queries/bulkTrades.queries';
import type { BulkRebalanceRun } from '../types/run.types';
import { canProceedToBulkTrader } from '../utils/presentation/runProgress';

export type UseBulkRebalanceProceedToBulkTraderParams = {
  runId: string | undefined;
  run: BulkRebalanceRun | null;
  succeededCount: number;
  canWriteBulkTradeRun: boolean;
  organizationId: string | undefined;
};

export type UseBulkRebalanceProceedToBulkTraderResult = {
  canProceed: boolean;
  proceeding: boolean;
  onProceedToBulkTrader: () => void;
};

export const useBulkRebalanceProceedToBulkTrader = ({
  runId,
  run,
  succeededCount,
  canWriteBulkTradeRun,
  organizationId,
}: UseBulkRebalanceProceedToBulkTraderParams): UseBulkRebalanceProceedToBulkTraderResult => {
  const navigate = useNavigate();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const useBulkTraderRevamp = useFeatureFlag(FlagsmithFlags.bulk_trader_revamp);

  const canProceed =
    !!run &&
    canProceedToBulkTrader(run.state, run.mode, succeededCount) &&
    canWriteBulkTradeRun;

  const [generateBulkTrades, { loading: proceeding }] = useMutation(
    GENERATE_BULK_TRADES,
    {
      onCompleted: (data) => {
        const bulkTradeRunId = data?.generateBulkTrades?.bulkTradeRun?.id;
        if (!bulkTradeRunId) {
          return;
        }

        navigate(
          getBulkTradeRunDetailPath(bulkTradeRunId, useBulkTraderRevamp),
        );
      },
      onError: (error) => {
        enqueueToast(error.message, { variant: 'error' });
      },
    },
  );

  const onProceedToBulkTrader = () => {
    if (!runId || !canProceed) {
      return;
    }

    sendAnalytic(ovAnalyticsEvents.portfolioOptimizerDetailSave, {
      optimizerRunId: runId,
      organizationId,
      timestamp: new Date().toISOString(),
    });

    // Scoped to this optimizer run — not the org-wide generateBulkTrades path
    // that would also pick up READY STRs from later manual createSTRs.
    generateBulkTrades({
      variables: {
        input: {
          autoFillActualQuantity: false,
          organizationId,
          portfolioOptimizerId: runId,
        },
      },
    });
  };

  return {
    canProceed,
    proceeding,
    onProceedToBulkTrader,
  };
};
